import { Injectable, inject } from '@angular/core';
import { Auth, authState, signInWithEmailAndPassword, signOut, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from '@angular/fire/auth';
import { Database, ref, objectVal } from '@angular/fire/database';
import { map, Observable, of, switchMap, firstValueFrom, filter } from 'rxjs';
import { User } from '../../shared/models/user.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  currentUser$: Observable<User | null>;
  private database = inject(Database);

  constructor(private auth: Auth) {
    this.currentUser$ = authState(this.auth).pipe(
      switchMap(firebaseUser => {
        if (firebaseUser) {
          const userRef = ref(this.database, `users/${firebaseUser.uid}`);
          return objectVal<User>(userRef).pipe(
            // This is the fix: ensure we don't emit null while the DB is fetching.
            filter((user): user is User => user !== null && user !== undefined),
            map(user => ({ ...user, uid: firebaseUser.uid }))
          );
        } else {
          return of(null);
        }
      })
    );
  }

  login(email: string, password: string) {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  logout() {
    return signOut(this.auth);
  }

  waitForUser(): Promise<User | null> {
    return firstValueFrom(this.currentUser$.pipe(filter(user => user !== undefined)));
  }

  // Note: The register method was removed as user creation is now handled by a cloud function.
  // If you need a client-side registration, it should be added here,
  // but it would call the `createUser` cloud function.

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No user is currently signed in or user has no email.');
    }

    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }

  hasRole(user: User | null, role: 'Administrator' | 'Coordinator' | 'Technician'): boolean {
    return !!user && user.role === role;
  }

  getUserRole(): Observable<'Administrator' | 'Coordinator' | 'Technician' | null> {
    return this.currentUser$.pipe(
      map(user => user ? user.role : null)
    );
  }
}
